import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  HardDrive,
  MemoryStick,
  Search,
  Server,
  SearchX,
  Server as ServerIcon,
} from 'lucide-react';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { EmptyState, PageHeader } from '@/components/page-header';
import { QueryState } from '@/components/query-state';
import { ServerCard, serverAddress, type CardState } from '@/components/server-card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { bytes } from '@/lib/format';
import { myServersQuery } from '@/lib/server-queries';
import { useAuthenticatedSession } from '@/lib/session';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_app/')({
  validateSearch: (s: Record<string, unknown>): { all?: boolean } =>
    s.all === true || s.all === 'true' ? { all: true } : {},
  component: Dashboard,
});

type Filter = 'all' | 'online' | 'offline';
type Sort = 'name' | 'game' | 'node';

const MIB = 1024 ** 2;

function Dashboard() {
  const { user } = useAuthenticatedSession();
  const { all } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const showAll = Boolean(all && user.isAdmin);
  const servers = useQuery(myServersQuery(showAll ? 'all' : 'mine'));
  const [term, setTerm] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [game, setGame] = useState<string>('all');
  const [sort, setSort] = useState<Sort>('name');
  // Live states reported by each card's socket; drives the counts and the status filter.
  const [states, setStates] = useState<Record<string, CardState>>({});
  const onState = useCallback(
    (id: string, state: CardState) =>
      setStates((s) => (s[id] === state ? s : { ...s, [id]: state })),
    [],
  );

  const list = useMemo(() => servers.data?.data ?? [], [servers.data]);
  // Games present in this list, so the filter only offers what exists.
  const games = useMemo(() => {
    const seen = new Map<string, string>();
    for (const s of list) {
      seen.set(s.profile.slug, s.profile.identity.name);
    }
    return [...seen];
  }, [list]);
  const sorted = useMemo(() => {
    const byName = (a: (typeof list)[number], b: (typeof list)[number]) =>
      a.name.localeCompare(b.name);
    return [...list].sort((a, b) =>
      sort === 'game'
        ? a.profile.identity.name.localeCompare(b.profile.identity.name) || byName(a, b)
        : sort === 'node'
          ? a.node.name.localeCompare(b.node.name) || byName(a, b)
          : byName(a, b),
    );
  }, [list, sort]);
  const counts = useMemo(() => {
    const vals = list.map((s) => states[s.identifier] ?? null);
    return {
      total: list.length,
      online: vals.filter((v) => v === 'running' || v === 'starting' || v === 'stopping').length,
      offline: vals.filter((v) => v === 'offline' || v === 'unreachable').length,
      memoryMb: list.reduce((n, s) => n + s.limits.memoryMb, 0),
      diskMb: list.reduce((n, s) => n + s.limits.diskMb, 0),
      unlimitedMemory: list.some((s) => s.limits.memoryMb === 0),
      unlimitedDisk: list.some((s) => s.limits.diskMb === 0),
    };
  }, [list, states]);

  const q = term.trim().toLowerCase();
  const matches = (s: (typeof list)[number]) => {
    if (q) {
      const hay = [
        s.name,
        s.identifier,
        s.egg.name,
        s.node.name,
        s.owner.username,
        serverAddress(s),
      ]
        .join(' ')
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (game !== 'all' && s.profile.slug !== game) return false;
    const st = states[s.identifier] ?? null;
    if (filter === 'online') return st === 'running' || st === 'starting' || st === 'stopping';
    if (filter === 'offline') return st === 'offline' || st === 'unreachable';
    return true;
  };

  return (
    <>
      <PageHeader
        title={showAll ? 'All servers' : 'Your servers'}
        icon={ServerIcon}
        description={
          showAll
            ? 'Every server on this panel, with live state from its node.'
            : 'Servers you own or have been given access to.'
        }
        actions={
          user.isAdmin && (
            <div className="bg-card shadow-card flex h-9 items-center gap-2 rounded-md border px-3">
              <Switch
                id="show-all"
                checked={showAll}
                onCheckedChange={(v) => void navigate({ search: v ? { all: true } : {} })}
              />
              <Label htmlFor="show-all">Show every server</Label>
            </div>
          )
        }
      />
      <QueryState
        query={servers}
        skeleton={
          <div className="grid gap-6" aria-busy>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-20 rounded-xl" />
              ))}
            </div>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-52 rounded-xl" />
              ))}
            </div>
          </div>
        }
      >
        {({ data }) =>
          data.length === 0 ? (
            <EmptyState
              icon={Server}
              title="No servers yet"
              action={
                user.isAdmin && (
                  <Button asChild size="sm">
                    <Link to="/admin/servers/new">Create a server</Link>
                  </Button>
                )
              }
            >
              Servers you own or have been given access to will appear here.
              {user.isAdmin
                ? showAll
                  ? ' There are no servers on this panel yet.'
                  : ' Turn on “Show every server” to see everyone’s, or create one.'
                : ' Ask an administrator to set one up for you.'}
            </EmptyState>
          ) : (
            <div className="grid gap-6">
              <section
                aria-label="Summary"
                className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5"
              >
                <Stat label="Servers" value={counts.total} />
                <Stat label="Online" value={counts.online} tone="success" />
                <Stat label="Offline" value={counts.offline} tone="destructive" />
                <Stat
                  label="Memory allocated"
                  icon={<MemoryStick className="size-3.5" aria-hidden />}
                  value={bytes(counts.memoryMb * MIB, 0)}
                  detail={counts.unlimitedMemory ? 'plus unlimited servers' : undefined}
                />
                <Stat
                  label="Disk allocated"
                  icon={<HardDrive className="size-3.5" aria-hidden />}
                  value={bytes(counts.diskMb * MIB, 0)}
                  detail={counts.unlimitedDisk ? 'plus unlimited servers' : undefined}
                />
              </section>

              <div className="flex flex-wrap items-center gap-3">
                <div className="relative w-full sm:max-w-xs">
                  <Search
                    className="text-subtle pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
                    aria-hidden
                  />
                  <Input
                    className="pl-9"
                    placeholder="Search name, game, node or address"
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    aria-label="Search servers"
                  />
                </div>
                <div
                  role="radiogroup"
                  aria-label="Filter by status"
                  className="bg-card shadow-card inline-flex h-9 items-center rounded-md border p-0.5"
                >
                  {(['all', 'online', 'offline'] as const).map((f) => (
                    <button
                      key={f}
                      type="button"
                      role="radio"
                      aria-checked={filter === f}
                      onClick={() => setFilter(f)}
                      className={cn(
                        'focus-visible:ring-ring/50 h-full rounded-[5px] px-3 text-sm font-medium capitalize transition-colors outline-none focus-visible:ring-[3px]',
                        filter === f
                          ? 'bg-elevated text-foreground'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {f}
                    </button>
                  ))}
                </div>
                {games.length > 1 && (
                  <div
                    role="radiogroup"
                    aria-label="Filter by game"
                    className="bg-card shadow-card inline-flex h-9 max-w-full items-center overflow-x-auto rounded-md border p-0.5"
                  >
                    {([['all', 'All games'], ...games] as [string, string][]).map(([id, label]) => (
                      <button
                        key={id}
                        type="button"
                        role="radio"
                        aria-checked={game === id}
                        onClick={() => setGame(id)}
                        className={cn(
                          'focus-visible:ring-ring/50 h-full shrink-0 rounded-[5px] px-3 text-sm font-medium transition-colors outline-none focus-visible:ring-[3px]',
                          game === id
                            ? 'bg-elevated text-foreground'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
                <label className="text-muted-foreground flex items-center gap-2 text-sm">
                  Sort
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value as Sort)}
                    className="bg-card h-9 rounded-md border px-2 text-sm"
                  >
                    <option value="name">Name</option>
                    <option value="game">Game</option>
                    <option value="node">Node</option>
                  </select>
                </label>
                {data.length < servers.data!.meta.total && (
                  <p className="text-muted-foreground text-sm">
                    Showing the first {data.length} of {servers.data!.meta.total}.
                  </p>
                )}
              </div>

              {/* Every card stays mounted (hidden when filtered out) so its live state keeps
                  feeding the counts. */}
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {sorted.map((s) => (
                  <div key={s.id} className={cn(!matches(s) && 'hidden')}>
                    <ServerCard server={s} onState={onState} />
                  </div>
                ))}
              </div>
              {!data.some(matches) && (
                <EmptyState icon={SearchX} title="No servers match">
                  Try a different search or status filter.
                </EmptyState>
              )}
            </div>
          )
        }
      </QueryState>
    </>
  );
}

function Stat({
  label,
  value,
  tone,
  icon,
  detail,
}: {
  label: string;
  value: ReactNode;
  tone?: 'success' | 'destructive';
  icon?: ReactNode;
  detail?: string;
}) {
  return (
    <div className="bg-card shadow-card grid gap-1 rounded-xl border px-4 py-3">
      <p className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium">
        {tone && (
          <span
            aria-hidden
            className={cn(
              'size-1.5 rounded-full',
              tone === 'success' ? 'bg-success' : 'bg-destructive',
            )}
          />
        )}
        {icon}
        {label}
      </p>
      <p className="text-xl font-semibold tracking-tight tabular-nums">{value}</p>
      {detail && <p className="text-muted-foreground text-xs">{detail}</p>}
    </div>
  );
}
